import { DialogComponent } from '@theme/dialog';

/**
 * A custom element that displays a product popup dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 * @property {HTMLElement} container – The content container element.
 *
 * @extends DialogComponent
 */
class ProductPopupDialog extends DialogComponent {
  requiredRefs = ['dialog', 'container'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {string[]} */
  #currentImages = [];

  /**
   * @typedef {object} CardProductData
   * @property {string} url - The product URL.
   * @property {string} title - The full product title.
   * @property {string} vendor - The product vendor.
   * @property {string} price - The formatted product price.
   * @property {string} year - The vip.year metafield value.
   * @property {string} excerpt - The vip.excerpt metafield value.
   */

  /**
   * Opens the popup with product data from the given URL.
   *
   * @param {string} productUrl - The URL of the product page to fetch.
   * @param {CardProductData} [cardData] - Optional product data from the card.
   */
  async openWithProduct(productUrl, cardData) {
    this.showDialog();
    this.#renderLoading();

    const productData = await this.#fetchProductData(productUrl, cardData);

    if (productData) {
      this.#renderProduct(productData);
    }
  }

  /**
   * Renders a loading state.
   */
  #renderLoading() {
    const container = /** @type {HTMLElement} */ (this.refs.container);
    if (!container) return;

    container.innerHTML = `
      <div class="product-popup-dialog__loading">
        Loading...
      </div>
    `;
  }

  /**
   * Fetches product data from the Shopify JSON API.
   *
   * @param {string} productUrl - The URL of the product page.
   * @param {CardProductData} [cardData] - Optional product data from the card.
   * @returns {Promise<ProductData | null>} The extracted product data.
   */
  async #fetchProductData(productUrl, cardData) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      // Extract product handle from URL
      const url = new URL(productUrl, window.location.origin);
      const pathParts = url.pathname.split('/');
      const productsIndex = pathParts.indexOf('products');
      const productHandle = productsIndex !== -1 ? pathParts[productsIndex + 1]?.split('?')[0] : null;

      if (!productHandle) {
        throw new Error('Could not extract product handle from URL');
      }

      // Fetch product JSON from Shopify API for images
      const jsonUrl = `/products/${productHandle}.js`;
      const response = await fetch(jsonUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product: HTTP error ${response.status}`);
      }

      const product = await response.json();
      return this.#extractProductData(product, cardData);
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') {
        return null;
      }
      console.error('Error fetching product data:', error);
      return null;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * @typedef {object} ProductData
   * @property {string} vendor - The product vendor.
   * @property {string} title - The product title (without vendor).
   * @property {string} year - The vip.year metafield value.
   * @property {string} excerpt - The vip.excerpt metafield value.
   * @property {string} price - The formatted product price.
   * @property {string[]} images - Array of product image URLs.
   */

  /**
   * Extracts product data from the Shopify product JSON.
   *
   * @param {object} product - The Shopify product object.
   * @param {CardProductData} [cardData] - Optional product data from the card.
   * @returns {ProductData} The extracted product data.
   */
  #extractProductData(product, cardData) {
    // Use card data if available, otherwise extract from product JSON
    let vendor = '';
    let title = '';
    let year = '';
    let excerpt = '';
    let artist_vat = '';
    let price = '';

    if (cardData && cardData.title) {
      // Get vendor from card data
      vendor = cardData.vendor || '';

      // Extract title without vendor name (format is "Vendor, Title")
      const fullTitle = cardData.title;
      const vendorPart = vendor ? `${vendor}, ` : '';
      title = fullTitle.startsWith(vendorPart) ? fullTitle.slice(vendorPart.length) : fullTitle;

      // Use metafields from card data
      year = cardData.year || '';
      excerpt = cardData.excerpt || '';
      artist_vat = cardData.artistVat || '';
      price = cardData.price || '';
    } else {
      // Fallback to product JSON
      vendor = /** @type {string} */ (product.vendor) || '';
      const fullTitle = /** @type {string} */ (product.title) || '';
      const titleParts = fullTitle.split(', ');
      title = titleParts.length > 1 ? titleParts.slice(1).join(', ') : fullTitle;

      // Format price from product JSON
      const priceInCents = /** @type {number} */ (product.price) || 0;
      price = this.#formatPrice(priceInCents);
    }

    // Extract images from product JSON
    /** @type {string[]} */
    const images = (/** @type {string[]} */ (product.images) || []).map((img) => this.#getLargeImageUrl(img));

    return { vendor, title, year, excerpt, artist_vat, price, images };
  }

  /**
   * Formats a price in cents to a display string.
   *
   * @param {number} priceInCents - The price in cents.
   * @returns {string} The formatted price.
   */
  #formatPrice(priceInCents) {
    const price = priceInCents / 100;
    // Use the store's currency format
    const formatter = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR',
    });
    return formatter.format(price);
  }

  /**
   * Converts an image URL to a larger version.
   *
   * @param {string} url - The original image URL.
   * @returns {string} The large image URL.
   */
  #getLargeImageUrl(url) {
    // Remove any existing size parameters and set to a large size
    return url.replace(/(_\d+x\d*|\d+x\d*_|_\d+x|x\d+_)/g, '').replace(/\?.*$/, '') + '?width=1200';
  }

  /**
   * Renders the product content in the popup.
   *
   * @param {ProductData} productData - The product data to render.
   */
  #renderProduct(productData) {
    const container = /** @type {HTMLElement} */ (this.refs.container);
    if (!container) return;

    const { vendor, title, year, excerpt, artist_vat, price, images } = productData;
    console.log(productData);

    this.#currentImages = images;

    const mainImage = images[0] || '';
    const otherImages = images.slice(1);

    // Build title with year
    const titleDisplay = year ? `<span>${title}</span>, ${year}` : title;

    container.innerHTML = `
      <div class="product-popup-dialog__content">
        <div class="product-popup-dialog__image-container">
          ${mainImage ? `
            <img
              src="${mainImage}"
              alt="${title}"
              class="product-popup-dialog__main-image"
              data-image-index="0"
              on:click="/handleImageClick"
            />
          ` : ''}
        </div>
        <div class="product-popup-dialog__details">
          ${vendor ? `<h2 class="product-popup-dialog__vendor">${vendor}</h2>` : ''}
          <p class="product-popup-dialog__title">${titleDisplay}</p>
          ${excerpt ? `<div class="product-popup-dialog__excerpt">${excerpt}</div>` : ''}
          <div class="product-popup-dialog__price">${price} ${artist_vat}</div>

          ${otherImages.length > 0 ? `
          <div class="product-popup-dialog__image-grid">
            ${otherImages.map((img, index) => `
              <img
                src="${img}"
                alt="${title}"
                class="product-popup-dialog__grid-image"
                data-image-index="${index + 1}"
                on:click="/handleImageClick"
              />
            `).join('')}
          </div>
        ` : ''}
        </div>
      </div>
    `;
  }

  /**
   * Handles clicks on product images to open the lightbox.
   *
   * @param {MouseEvent} event - The click event.
   */
  handleImageClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLImageElement)) return;

    const index = parseInt(target.dataset.imageIndex || '0', 10);
    this.#openLightbox(index);
  };

  /**
   * Opens the lightbox at the specified image index.
   *
   * @param {number} index - The index of the image to display.
   */
  #openLightbox(index) {
    const lightbox = document.getElementById('product-popup-lightbox');
    if (!lightbox || typeof lightbox.openAtIndex !== 'function') return;

    lightbox.openAtIndex(index, this.#currentImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }
}

if (!customElements.get('product-popup-dialog')) {
  customElements.define('product-popup-dialog', ProductPopupDialog);
}

export { ProductPopupDialog };
